import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Users, Brain, TrendingUp, Activity, Calendar, AlertCircle, RefreshCw, BookOpen, Search, ChevronLeft, ChevronRight, Gift, Briefcase, CreditCard, FileText, Download } from 'lucide-react';
import { toast } from 'sonner';
import AdminCouponManagement from './AdminCouponManagement';
import AdminCarousel from './AdminCarousel';
import AdminOfferZone from './AdminOfferZone';
import AdminPlanPricing from './AdminPlanPricing';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    total_users: 0,
    active_users: 0,
    completed_profiles: 0,
    total_ai_queries: 0,
    recent_registrations: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [testUsersLoading, setTestUsersLoading] = useState(false);
  const [syncingExams, setSyncingExams] = useState(false);
  const [syncingEngineering, setSyncingEngineering] = useState(false);
  const [syncingGlobal, setSyncingGlobal] = useState(false);
  
  // User Management State
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Enrollment State
  const [enrollmentFeatures, setEnrollmentFeatures] = useState(['all']);
  const [enrollmentDays, setEnrollmentDays] = useState(30);
  const [enrolling, setEnrolling] = useState(false);
  
  // Credit Management State
  const [creditAmount, setCreditAmount] = useState(1500);
  const [creditValidityDays, setCreditValidityDays] = useState(30);
  const [grantingCredits, setGrantingCredits] = useState(false);
  const [togglingAccess, setTogglingAccess] = useState(false);
  
  // Need Support State
  const [supportUsers, setSupportUsers] = useState([]);
  const [supportUsersLoading, setSupportUsersLoading] = useState(false);
  const [selectedSupportUser, setSelectedSupportUser] = useState(null);

  // LMS Enrollments State
  const [userLmsEnrollments, setUserLmsEnrollments] = useState([]);
  const [lmsEnrollmentsLoading, setLmsEnrollmentsLoading] = useState(false);

  // Employer Management State
  const [employers, setEmployers] = useState([]);
  const [employersLoading, setEmployersLoading] = useState(false);
  const [employerSearchQuery, setEmployerSearchQuery] = useState('');
  const [employerCurrentPage, setEmployerCurrentPage] = useState(1);
  const [employerTotalPages, setEmployerTotalPages] = useState(1);
  const [employerStatusFilter, setEmployerStatusFilter] = useState('all');
  const [selectedEmployer, setSelectedEmployer] = useState(null);
  const [approvingEmployer, setApprovingEmployer] = useState(false);

  // Payment History State
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentSearchQuery, setPaymentSearchQuery] = useState('');
  const [paymentCurrentPage, setPaymentCurrentPage] = useState(1);
  const [paymentTotalPages, setPaymentTotalPages] = useState(1);
  const [paymentTotalItems, setPaymentTotalItems] = useState(0);
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('all');

  useEffect(() => {
    fetchStats();
    fetchUsers();
    fetchSupportUsers();
    fetchEmployers();
    fetchPayments();
  }, []);

  useEffect(() => {
    // Debounce search
    const timer = setTimeout(() => {
      fetchUsers();
    }, 500);
    
    return () => clearTimeout(timer);
  }, [searchQuery, currentPage]);

  useEffect(() => {
    // Debounce employer search
    const timer = setTimeout(() => {
      fetchEmployers();
    }, 500);
    
    return () => clearTimeout(timer);
  }, [employerSearchQuery, employerCurrentPage, employerStatusFilter]);

  useEffect(() => {
    // Debounce payment search
    const timer = setTimeout(() => {
      fetchPayments();
    }, 500);
    
    return () => clearTimeout(timer);
  }, [paymentSearchQuery, paymentCurrentPage, paymentStatusFilter]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication required');
        toast.error('Please login to access admin dashboard');
        return;
      }
      
      const response = await axios.get(`${API}/admin/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setStats(response.data);
      setError(null);
    } catch (error) {
      console.error('Failed to fetch admin stats:', error);
      if (error.response?.status === 403) {
        setError('Access denied. Admin privileges required.');
        toast.error('Access denied. Admin privileges required.');
      } else {
        setError('Failed to load admin statistics');
        toast.error('Failed to load admin statistics');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      setUsersLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required');
        return;
      }
      
      const params = {
        page: currentPage,
        limit: 10
      };
      
      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }
      
      const response = await axios.get(`${API}/admin/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        params
      });
      
      if (response.data.success) {
        setUsers(response.data.users);
        setTotalPages(response.data.pagination.total_pages);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
      if (error.response?.status === 403) {
        toast.error('Access denied. Admin privileges required.');
      } else {
        toast.error('Failed to load users');
      }
    } finally {
      setUsersLoading(false);
    }
  };

  // Fetch users who need support (crisis flagged) - uses dedicated fast endpoint
  const fetchSupportUsers = async () => {
    try {
      setSupportUsersLoading(true);
      const token = localStorage.getItem('token');
      
      // Use dedicated endpoint for crisis-flagged users - much faster
      const response = await axios.get(`${API}/admin/support-users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setSupportUsers(response.data.users || []);
    } catch (error) {
      console.error('Failed to fetch support users:', error);
      setSupportUsers([]);
    } finally {
      setSupportUsersLoading(false);
    }
  };

  // Fetch employers
  const fetchEmployers = async () => {
    try {
      setEmployersLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required');
        return;
      }
      
      const params = {
        page: employerCurrentPage,
        limit: 10
      };
      
      if (employerSearchQuery.trim()) {
        params.search = employerSearchQuery.trim();
      }
      
      if (employerStatusFilter !== 'all') {
        params.status_filter = employerStatusFilter;
      }
      
      const response = await axios.get(`${API}/admin/employers`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        params
      });
      
      if (response.data.success) {
        setEmployers(response.data.employers);
        setEmployerTotalPages(response.data.pagination.total_pages);
      }
    } catch (error) {
      console.error('Failed to fetch employers:', error);
      if (error.response?.status === 403) {
        toast.error('Access denied. Admin privileges required.');
      } else {
        toast.error('Failed to load employers');
      }
    } finally {
      setEmployersLoading(false);
    }
  };

  // Fetch all payments (admin)
  const fetchPayments = async () => {
    try {
      setPaymentsLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required');
        return;
      }
      
      const params = {
        page: paymentCurrentPage,
        limit: 20
      };
      
      if (paymentSearchQuery.trim()) {
        params.search = paymentSearchQuery.trim();
      }
      
      if (paymentStatusFilter !== 'all') {
        params.status_filter = paymentStatusFilter;
      }
      
      const response = await axios.get(`${API}/admin/payments`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        params
      });
      
      if (response.data.success) {
        setPayments(response.data.payments);
        setPaymentTotalPages(response.data.pagination.total_pages);
        setPaymentTotalItems(response.data.pagination.total_items);
      }
    } catch (error) {
      console.error('Failed to fetch payments:', error);
      if (error.response?.status === 403) {
        toast.error('Access denied. Admin privileges required.');
      } else {
        toast.error('Failed to load payments');
      }
    } finally {
      setPaymentsLoading(false);
    }
  };

  // Approve employer
  const handleApproveEmployer = async (employerId) => {
    try {
      setApprovingEmployer(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `${API}/admin/employers/${employerId}/approve`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.data.success) {
        toast.success('Employer approved successfully!');
        fetchEmployers();
        if (selectedEmployer?.id === employerId) {
          setSelectedEmployer({ ...selectedEmployer, employer_status: 'approved' });
        }
      }
    } catch (error) {
      console.error('Approve employer failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to approve employer');
    } finally {
      setApprovingEmployer(false);
    }
  };

  // Reject employer
  const handleRejectEmployer = async (employerId) => {
    try {
      setApprovingEmployer(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `${API}/admin/employers/${employerId}/reject`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.data.success) {
        toast.success('Employer rejected successfully!');
        fetchEmployers();
        if (selectedEmployer?.id === employerId) {
          setSelectedEmployer({ ...selectedEmployer, employer_status: 'rejected' });
        }
      }
    } catch (error) {
      console.error('Reject employer failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to reject employer');
    } finally {
      setApprovingEmployer(false);
    }
  };

  // Clear crisis flag for a user
  const handleClearCrisisFlag = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/admin/clear-crisis-flag/${userId}`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      toast.success('Support flag cleared - user marked as addressed');
      fetchSupportUsers();
      fetchUsers();
      setSelectedSupportUser(null);
    } catch (error) {
      console.error('Failed to clear crisis flag:', error);
      toast.error('Failed to clear support flag');
    }
  };

  // Fetch user's LMS enrollments
  const fetchUserLmsEnrollments = async (userId) => {
    try {
      setLmsEnrollmentsLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/lms/admin/user-enrollments/${userId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.data.success) {
        setUserLmsEnrollments(response.data.data.enrollments || []);
      }
    } catch (error) {
      console.error('Failed to fetch LMS enrollments:', error);
      setUserLmsEnrollments([]);
    } finally {
      setLmsEnrollmentsLoading(false);
    }
  };

  const handleEnrollUser = async () => {
    if (!selectedUser) return;
    
    try {
      setEnrolling(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `${API}/admin/enroll-user`,
        {
          user_id: selectedUser.id,
          features: enrollmentFeatures,
          days: enrollmentDays
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.data.success) {
        toast.success(`User enrolled successfully for ${enrollmentDays} days!`);
        // Reset form
        setEnrollmentFeatures(['all']);
        setEnrollmentDays(30);
        // Refresh users
        fetchUsers();
      }
    } catch (error) {
      console.error('Enrollment failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to enroll user');
    } finally {
      setEnrolling(false);
    }
  };

  const handleGrantCredits = async () => {
    if (!selectedUser || creditAmount <= 0) {
      toast.error('Please enter a valid credit amount');
      return;
    }

    setGrantingCredits(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/admin/grant-credits`,
        {
          user_id: selectedUser.id,
          credits: creditAmount,
          validity_days: creditValidityDays
        },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        toast.success(`Successfully granted ${creditAmount} credits!`);
        window.dispatchEvent(new Event('creditUpdated')); // Trigger live update
        fetchUsers(); // Refresh user list
        // Update selected user's credit display
        if (selectedUser) {
          setSelectedUser({
            ...selectedUser,
            nerd_ai_credits: response.data.new_balance
          });
        }
        setCreditAmount(1500); // Reset to default
      }
    } catch (error) {
      console.error('Grant credits error:', error);
      toast.error(error.response?.data?.detail || 'Failed to grant credits');
    } finally {
      setGrantingCredits(false);
    }
  };

  const downloadInvoice = async (orderId, userId = null, format = 'html') => {
    try {
      const token = localStorage.getItem('token');
      const url = userId 
        ? `${API}/admin/invoice/${orderId}/${userId}`
        : `${API}/payments/invoice/${orderId}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        if (format === 'pdf') {
          // Open in new window for PDF printing
          const newWindow = window.open('', '_blank');
          newWindow.document.write(response.data.invoice_html);
          newWindow.document.close();
          setTimeout(() => {
            newWindow.print();
          }, 250);
        } else {
          // HTML download
          const blob = new Blob([response.data.invoice_html], { type: 'text/html' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `invoice_${orderId}.html`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
          toast.success('Invoice downloaded');
        }
      }
    } catch (error) {
      console.error('Failed to download invoice:', error);
      toast.error('Failed to download invoice');
    }
  };

  const handleToggleUserAccess = async () => {
    if (!selectedUser) return;
    
    try {
      setTogglingAccess(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `${API}/admin/toggle-user-access`,
        { user_id: selectedUser.id },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.data.success) {
        toast.success(response.data.message);
        // Update selected user state
        setSelectedUser({
          ...selectedUser,
          is_active: response.data.is_active
        });
        // Refresh users list
        fetchUsers();
      }
    } catch (error) {
      console.error('Toggle access failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to toggle user access');
    } finally {
      setTogglingAccess(false);
    }
  };



  const handleCleanupTestUsers = async () => {
    if (!window.confirm('⚠️ This will permanently delete all test/dummy users (emails with test, demo, example, etc.). Continue?')) {
      return;
    }

    setTestUsersLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/admin/cleanup-test-users`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        const deleted = response.data.deleted;
        if (deleted > 0) {
          toast.success(`✅ Deleted ${deleted} test users!`);
          alert(`✅ Cleanup Complete!\n\nDeleted:\n- ${response.data.details.users} users\n- ${response.data.details.profiles} profiles\n- ${response.data.details.messages} messages\n- ${response.data.details.friendships} friendships`);
          // Refresh stats
          fetchStats();
        } else {
          toast.info('No test users found to delete');
        }
      }
    } catch (error) {
      console.error('Test user cleanup failed:', error);
      toast.error(error.response?.data?.detail || 'Cleanup failed. Please try again.');
    } finally {
      setTestUsersLoading(false);
    }
  };

  const handleSyncAllExamBanks = async () => {
    try {
      setSyncingExams(true);
      toast.info('Syncing all medical/dental exam banks... This may take a few minutes');
      
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/exam-bank/admin/sync-all`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.data.success) {
        const successCount = response.data.results.filter(r => r.status === 'success').length;
        toast.success(`Successfully synced ${successCount} exam banks!`);
      }
    } catch (error) {
      console.error('Error syncing exam banks:', error);
      toast.error('Failed to sync exam banks');
    } finally {
      setSyncingExams(false);
    }
  };

  const handleSyncEngineeringBanks = async () => {
    try {
      setSyncingEngineering(true);
      toast.info('Syncing IIT-JEE & NEET-UG banks...');
      
      const token = localStorage.getItem('token');
      
      // Sync IIT-JEE
      await axios.post(
        `${API}/exam-bank/admin/sync/IIT_JEE_Success_Bank`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      // Sync NEET-UG
      await axios.post(
        `${API}/exam-bank/admin/sync/NEET_UG_Success_Bank`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      toast.success('Successfully synced engineering exam banks!');
    } catch (error) {
      console.error('Error syncing engineering banks:', error);
      toast.error('Failed to sync engineering banks');
    } finally {
      setSyncingEngineering(false);
    }
  };

  const handleSyncGlobalBanks = async () => {
    try {
      setSyncingGlobal(true);
      toast.info('Syncing GRE & GMAT banks...');
      
      const token = localStorage.getItem('token');
      
      // Sync GRE
      await axios.post(
        `${API}/exam-bank/admin/sync/GRE_Success_Bank`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      // Sync GMAT
      await axios.post(
        `${API}/exam-bank/admin/sync/GMAT_Success_Bank`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      toast.success('Successfully synced GRE & GMAT exam banks!');
    } catch (error) {
      console.error('Error syncing global banks:', error);
      toast.error('Failed to sync global banks');
    } finally {
      setSyncingGlobal(false);
    }
  };

  const handleCleanupChat = async () => {
    if (!window.confirm('⚠️ This will delete all old image URL messages from chat. Continue?')) {
      return;
    }

    setCleanupLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/admin/cleanup-image-urls`,
        { confirm: true },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        const deleted = response.data.stats.total_deleted;
        toast.success(`✅ Success! Deleted ${deleted} old messages. Chat is now stable!`);
        alert(`✅ Cleanup Complete!\n\nDeleted: ${deleted} messages\nChat should now be stable without image URL issues.`);
      }
    } catch (error) {
      console.error('Cleanup failed:', error);
      toast.error(error.response?.data?.detail || 'Cleanup failed. Please try again.');
    } finally {
      setCleanupLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Total Users',
      value: stats.total_users,
      icon: <Users className="h-6 w-6 text-blue-600" />,
      color: 'bg-blue-50',
      textColor: 'text-blue-900',
      testId: 'total-users-stat'
    },
    {
      title: 'Active Users',
      value: stats.active_users,
      icon: <Activity className="h-6 w-6 text-green-600" />,
      color: 'bg-green-50',
      textColor: 'text-green-900',
      testId: 'active-users-stat'
    },
    {
      title: 'Completed Profiles',
      value: stats.completed_profiles,
      percentage: stats.total_users > 0 ? Math.round((stats.completed_profiles / stats.total_users) * 100) : 0,
      icon: <TrendingUp className="h-6 w-6 text-purple-600" />,
      color: 'bg-purple-50',
      textColor: 'text-purple-900',
      testId: 'completed-profiles-stat'
    },
    {
      title: 'AI Queries',
      value: stats.total_ai_queries,
      icon: <Brain className="h-6 w-6 text-indigo-600" />,
      color: 'bg-indigo-50',
      textColor: 'text-indigo-900',
      testId: 'ai-queries-stat'
    },
    {
      title: 'Recent Registrations',
      value: stats.recent_registrations,
      subtitle: 'Last 7 days',
      icon: <Calendar className="h-6 w-6 text-orange-600" />,
      color: 'bg-orange-50',
      textColor: 'text-orange-900',
      testId: 'recent-registrations-stat'
    }
  ];

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-amber-50/30 p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="mb-4">
            <h1 className="text-2xl font-bold text-gray-900 mb-1" data-testid="admin-title">
              Admin Dashboard
            </h1>
            <p className="text-gray-600 text-sm">Monitor platform activity and user engagement</p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => window.location.href = '/admin/promos'}
              className="px-3 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              title="Manage promotional codes and discounts"
            >
              <span className="flex items-center gap-1.5">
                <Gift className="h-3.5 w-3.5" />
                Promo Codes
              </span>
            </button>
            <button
              onClick={() => {
                const el = document.getElementById('admin-plan-pricing-section');
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
              className="px-3 py-2 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
              title="Edit plan prices (INR + USD)"
              data-testid="admin-plan-pricing-link"
            >
              <span className="flex items-center gap-1.5">
                <CreditCard className="h-3.5 w-3.5" />
                Plan Pricing
              </span>
            </button>
            <button
              onClick={handleSyncAllExamBanks}
              disabled={syncingExams}
              className="px-3 py-2 text-sm bg-blue-700 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Sync all medical/dental exam banks from Google Sheets"
            >
              <span className="flex items-center gap-1.5">
                <RefreshCw className={`h-3.5 w-3.5 ${syncingExams ? 'animate-spin' : ''}`} />
                {syncingExams ? 'Syncing...' : 'Sync Medical/Dental'}
              </span>
            </button>
            <button
              onClick={handleSyncEngineeringBanks}
              disabled={syncingEngineering}
              className="px-3 py-2 text-sm bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Sync IIT-JEE & NEET-UG exam banks"
            >
              <span className="flex items-center gap-1.5">
                <RefreshCw className={`h-3.5 w-3.5 ${syncingEngineering ? 'animate-spin' : ''}`} />
                {syncingEngineering ? 'Syncing...' : 'Sync Engineering'}
              </span>
            </button>
            <button
              onClick={handleSyncGlobalBanks}
              disabled={syncingGlobal}
              className="px-3 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Sync GRE & GMAT exam banks"
            >
              <span className="flex items-center gap-1.5">
                <RefreshCw className={`h-3.5 w-3.5 ${syncingGlobal ? 'animate-spin' : ''}`} />
                {syncingGlobal ? 'Syncing...' : 'Sync GRE/GMAT'}
              </span>
            </button>
            <a 
              href="/test-prep-admin" 
              className="px-3 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              Manage Tests
            </a>
            <button
              onClick={handleCleanupTestUsers}
              disabled={testUsersLoading}
              className="px-3 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Remove all test/dummy users from the platform"
            >
              {testUsersLoading ? 'Removing...' : 'Remove Test Users'}
            </button>
            <button
              onClick={handleCleanupChat}
              disabled={cleanupLoading}
              className="px-3 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Clean up old image URLs from chat to fix stability issues"
            >
              {cleanupLoading ? 'Cleaning...' : 'Clean Chat'}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-600"></div>
          </div>
        ) : error ? (
          <Card className="card border-red-200 bg-red-50">
            <CardContent className="p-6 text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-red-900 mb-2">Error Loading Data</h3>
              <p className="text-red-700 mb-4">{error}</p>
              <button
                onClick={fetchStats}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                data-testid="retry-button"
              >
                Retry
              </button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 mb-8">
              {statCards.map((stat, index) => (
                <Card key={index} className="card" data-testid={stat.testId}>
                  <CardContent className="p-6">
                    <div className={`w-12 h-12 ${stat.color} rounded-xl flex items-center justify-center mb-4`}>
                      {stat.icon}
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-600 mb-1">{stat.title}</h3>
                      <div className="flex items-baseline space-x-2">
                        <p className={`text-2xl font-bold ${stat.textColor}`}>
                          {formatNumber(stat.value)}
                        </p>
                        {stat.percentage !== undefined && (
                          <Badge variant="secondary" className="text-xs">
                            {stat.percentage}%
                          </Badge>
                        )}
                      </div>
                      {stat.subtitle && (
                        <p className="text-xs text-gray-500 mt-1">{stat.subtitle}</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Additional Info Cards */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* User Engagement */}
              <Card className="card">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <TrendingUp className="h-5 w-5 text-purple-600" />
                    <span>User Engagement</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Profile Completion Rate</span>
                    <div className="flex items-center space-x-2">
                      <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-purple-500 transition-all duration-300"
                          style={{ 
                            width: `${stats.total_users > 0 ? (stats.completed_profiles / stats.total_users) * 100 : 0}%` 
                          }}
                        ></div>
                      </div>
                      <span className="text-sm font-medium text-gray-900">
                        {stats.total_users > 0 ? Math.round((stats.completed_profiles / stats.total_users) * 100) : 0}%
                      </span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Users with Complete Profiles</span>
                    <span className="font-medium">{stats.completed_profiles} / {stats.total_users}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Average Queries per User</span>
                    <span className="font-medium">
                      {stats.completed_profiles > 0 ? (stats.total_ai_queries / stats.completed_profiles).toFixed(1) : '0'}
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* Platform Health */}
              <Card className="card">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Activity className="h-5 w-5 text-green-600" />
                    <span>Platform Health</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">System Status</span>
                    <Badge className="bg-green-100 text-green-800">Operational</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">AI Service</span>
                    <Badge className="bg-blue-100 text-blue-800">Active</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Database</span>
                    <Badge className="bg-green-100 text-green-800">Connected</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Last Updated</span>
                    <span className="text-sm text-gray-500">{new Date().toLocaleTimeString()}</span>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Quick Actions */}
            <div className="mt-8">
              <Card className="card bg-gradient-to-r from-gray-50 to-blue-50 border-gray-200">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
                  <div className="flex flex-wrap gap-3">
                    <button 
                      onClick={fetchStats}
                      className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                      data-testid="refresh-stats-button"
                    >
                      Refresh Stats
                    </button>
                    <Badge variant="outline" className="px-3 py-1">Export Data (Coming Soon)</Badge>
                    <Badge variant="outline" className="px-3 py-1">Analytics (Coming Soon)</Badge>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* User Management Section */}
            <div className="mt-8">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-6 w-6 text-purple-600" />
                    User Management
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {/* Search Bar */}
                  <div className="mb-6">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search by name, email, or phone number..."
                        value={searchQuery}
                        onChange={(e) => {
                          setSearchQuery(e.target.value);
                          setCurrentPage(1); // Reset to first page on search
                        }}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  {/* User List Table */}
                  {usersLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                    </div>
                  ) : users.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <Users className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                      <p>No users found</p>
                    </div>
                  ) : (
                    <>
                      <div className="overflow-x-auto -mx-4 sm:mx-0">
                        <table className="w-full text-sm min-w-[800px]">
                          <thead className="bg-gray-50 border-b">
                            <tr>
                              <th className="text-left p-2 sm:p-3 font-semibold text-gray-700 whitespace-nowrap">Name</th>
                              <th className="text-left p-2 sm:p-3 font-semibold text-gray-700 whitespace-nowrap hidden sm:table-cell">Email</th>
                              <th className="text-left p-2 sm:p-3 font-semibold text-gray-700 whitespace-nowrap hidden md:table-cell">Phone</th>
                              <th className="text-left p-2 sm:p-3 font-semibold text-gray-700 whitespace-nowrap hidden lg:table-cell">College</th>
                              <th className="text-left p-2 sm:p-3 font-semibold text-gray-700 whitespace-nowrap">Role</th>
                              <th className="text-left p-2 sm:p-3 font-semibold text-gray-700 whitespace-nowrap">Plan Expiry</th>
                              <th className="text-left p-2 sm:p-3 font-semibold text-gray-700 whitespace-nowrap">Status</th>
                              <th className="text-left p-2 sm:p-3 font-semibold text-gray-700 whitespace-nowrap">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {users.map((user, index) => {
                              // Calculate days until expiry
                              const expiryDate = user.enrollment_expiry ? new Date(user.enrollment_expiry) : null;
                              const today = new Date();
                              const daysUntilExpiry = expiryDate ? Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24)) : null;
                              const isExpiringSoon = daysUntilExpiry !== null && daysUntilExpiry > 0 && daysUntilExpiry <= 30;
                              const isExpired = daysUntilExpiry !== null && daysUntilExpiry <= 0;
                              
                              return (
                              <tr key={user.id} className={`border-b hover:bg-gray-50 ${isExpiringSoon ? 'bg-orange-50' : ''} ${isExpired ? 'bg-red-50' : ''}`}>
                                <td className="p-2 sm:p-3">
                                  <div className="flex items-center gap-2">
                                    {user.avatar_url ? (
                                      <img 
                                        src={user.avatar_url} 
                                        alt={user.full_name}
                                        className="h-8 w-8 rounded-full object-cover flex-shrink-0" loading="lazy" decoding="async"/>
                                    ) : (
                                      <div className="h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                                        <span className="text-sm font-medium text-purple-600">
                                          {user.full_name.charAt(0).toUpperCase()}
                                        </span>
                                      </div>
                                    )}
                                    <div className="flex flex-col min-w-0">
                                      <span className="font-medium text-gray-900 truncate">{user.full_name}</span>
                                      <span className="text-xs text-gray-500 truncate sm:hidden">{user.email}</span>
                                      {user.crisis_flagged && (
                                        <Badge className="bg-red-500 text-white text-xs mt-1 animate-pulse">
                                          ⚠️ Needs Support
                                        </Badge>
                                      )}
                                      {isExpiringSoon && (
                                        <Badge className="bg-orange-500 text-white text-xs mt-1">
                                          ⏰ Expiring in {daysUntilExpiry} days
                                        </Badge>
                                      )}
                                      {isExpired && (
                                        <Badge className="bg-red-600 text-white text-xs mt-1">
                                          ❌ Plan Expired
                                        </Badge>
                                      )}
                                    </div>
                                  </div>
                                </td>
                                <td className="p-2 sm:p-3 text-gray-600 hidden sm:table-cell">{user.email}</td>
                                <td className="p-2 sm:p-3 text-gray-600 hidden md:table-cell">{user.phone}</td>
                                <td className="p-2 sm:p-3 text-gray-600 max-w-xs truncate hidden lg:table-cell" title={user.college}>
                                  {user.college}
                                </td>
                                <td className="p-2 sm:p-3">
                                  <Badge 
                                    className={
                                      user.role === 'Admin' 
                                        ? 'bg-red-100 text-red-700' 
                                        : user.role === 'Premium'
                                        ? 'bg-purple-100 text-purple-700'
                                        : 'bg-blue-100 text-blue-700'
                                    }
                                  >
                                    {user.role}
                                  </Badge>
                                </td>
                                <td className="p-2 sm:p-3">
                                  {expiryDate ? (
                                    <div className="flex flex-col">
                                      <span className={`font-medium text-xs sm:text-sm ${isExpired ? 'text-red-600' : isExpiringSoon ? 'text-orange-600' : 'text-gray-700'}`}>
                                        {expiryDate.toLocaleDateString('en-US', {
                                          month: 'short',
                                          day: 'numeric',
                                          year: 'numeric'
                                        })}
                                      </span>
                                      {daysUntilExpiry > 0 && (
                                        <span className={`text-xs ${isExpiringSoon ? 'text-orange-500 font-semibold' : 'text-gray-500'}`}>
                                          {daysUntilExpiry} days left
                                        </span>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-gray-400 text-xs sm:text-sm">No Plan</span>
                                  )}
                                </td>
                                <td className="p-2 sm:p-3">
                                  <Badge 
                                    className={
                                      user.is_active 
                                        ? 'bg-green-100 text-green-700' 
                                        : 'bg-yellow-100 text-yellow-700'
                                    }
                                  >
                                    {user.is_active ? 'Active' : 'Inactive'}
                                  </Badge>
                                </td>
                                <td className="p-2 sm:p-3">
                                  <button 
                                    onClick={() => {
                                      setSelectedUser(user);
                                      fetchUserLmsEnrollments(user.id);
                                    }}
                                    className="text-blue-600 hover:underline text-sm font-medium"
                                  >
                                    View Details
                                  </button>
                                </td>
                              </tr>
                            );})}
                          </tbody>
                        </table>
                      </div>

                      {/* Pagination */}
                      {totalPages > 1 && (
                        <div className="mt-6 flex items-center justify-between">
                          <p className="text-sm text-gray-600">
                            Page {currentPage} of {totalPages}
                          </p>
                          <div className="flex gap-2">
                            <button
                              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                              disabled={currentPage === 1}
                              className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                            >
                              <ChevronLeft className="h-4 w-4" />
                              Previous
                            </button>
                            <button
                              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                              disabled={currentPage === totalPages}
                              className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                            >
                              Next
                              <ChevronRight className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {/* User Details Modal */}
                  {selectedUser && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b sticky top-0 bg-white">
                          <div className="flex justify-between items-start">
                            <div className="flex items-center gap-3">
                              {selectedUser.avatar_url ? (
                                <img 
                                  src={selectedUser.avatar_url} 
                                  alt={selectedUser.full_name}
                                  className="h-16 w-16 rounded-full object-cover" loading="lazy" decoding="async"/>
                              ) : (
                                <div className="h-16 w-16 rounded-full bg-purple-100 flex items-center justify-center">
                                  <span className="text-2xl font-medium text-purple-600">
                                    {selectedUser.full_name.charAt(0).toUpperCase()}
                                  </span>
                                </div>
                              )}
                              <div>
                                <h2 className="text-2xl font-bold text-gray-900">{selectedUser.full_name}</h2>
                                <p className="text-gray-600">{selectedUser.email}</p>
                              </div>
                            </div>
                            <button 
                              onClick={() => setSelectedUser(null)}
                              className="text-gray-500 hover:text-gray-700 text-2xl"
                            >
                              ×
                            </button>
                          </div>
                        </div>

                        <div className="p-6 space-y-6">
                          {/* User Info */}
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-3">User Information</h3>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <p className="text-sm text-gray-600">Phone Number</p>
                                <p className="font-medium">{selectedUser.phone}</p>
                              </div>
                              <div>
                                <p className="text-sm text-gray-600">College</p>
                                <p className="font-medium">{selectedUser.college}</p>
                              </div>
                              <div>
                                <p className="text-sm text-gray-600">Degree</p>
                                <p className="font-medium">{selectedUser.degree}</p>
                              </div>
                              <div>
                                <p className="text-sm text-gray-600 mb-2">Status</p>
                                <div className="flex items-center gap-2">
                                  <Badge 
                                    className={
                                      selectedUser.is_active 
                                        ? 'bg-green-100 text-green-700' 
                                        : 'bg-red-100 text-red-700'
                                    }
                                  >
                                    {selectedUser.is_active ? 'Active' : 'Deactivated'}
                                  </Badge>
                                  {selectedUser.email !== 'shifracs@gmail.com' && (
                                    <button
                                      onClick={() => handleToggleUserAccess()}
                                      disabled={togglingAccess}
                                      className={`px-3 py-1 text-xs font-semibold rounded transition-colors ${
                                        selectedUser.is_active
                                          ? 'bg-red-500 hover:bg-red-600 text-white'
                                          : 'bg-green-500 hover:bg-green-600 text-white'
                                      } disabled:opacity-50`}
                                    >
                                      {togglingAccess ? '...' : (selectedUser.is_active ? 'OFF' : 'ON')}
                                    </button>
                                  )}
                                </div>
                              </div>
                              
                              {/* Plan Expiry Date */}
                              <div className="col-span-2 mt-2 p-4 rounded-lg border-2 border-purple-200 bg-purple-50">
                                <p className="text-sm text-gray-600 mb-2">Plan & Courses Expiry</p>
                                {selectedUser.enrollment_expiry ? (
                                  (() => {
                                    const expiryDate = new Date(selectedUser.enrollment_expiry);
                                    const today = new Date();
                                    const daysLeft = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
                                    const isExpiringSoon = daysLeft > 0 && daysLeft <= 30;
                                    const isExpired = daysLeft <= 0;
                                    
                                    return (
                                      <div className="flex items-center gap-3">
                                        <div className={`text-lg font-bold ${isExpired ? 'text-red-600' : isExpiringSoon ? 'text-orange-600' : 'text-green-600'}`}>
                                          {expiryDate.toLocaleDateString('en-US', {
                                            weekday: 'short',
                                            month: 'long',
                                            day: 'numeric',
                                            year: 'numeric'
                                          })}
                                        </div>
                                        {isExpired ? (
                                          <Badge className="bg-red-600 text-white animate-pulse">
                                            ❌ EXPIRED
                                          </Badge>
                                        ) : isExpiringSoon ? (
                                          <Badge className="bg-orange-500 text-white animate-pulse">
                                            ⏰ {daysLeft} days left - EXPIRING SOON
                                          </Badge>
                                        ) : (
                                          <Badge className="bg-green-600 text-white">
                                            ✅ {daysLeft} days remaining
                                          </Badge>
                                        )}
                                      </div>
                                    );
                                  })()
                                ) : (
                                  <span className="text-gray-500 font-medium">No active plan</span>
                                )}
                              </div>
                              
                              <div>
                                <p className="text-sm text-gray-600">Profile Completed</p>
                                <Badge 
                                  className={
                                    selectedUser.profile_completed 
                                      ? 'bg-green-100 text-green-700' 
                                      : 'bg-gray-100 text-gray-700'
                                  }
                                >
                                  {selectedUser.profile_completed ? 'Yes' : 'No'}
                                </Badge>
                              </div>
                              <div>
                                <p className="text-sm text-gray-600">Joined Date</p>
                                <p className="font-medium">
                                  {selectedUser.created_at 
                                    ? new Date(selectedUser.created_at).toLocaleDateString('en-US', {
                                        month: 'long',
                                        day: 'numeric',
                                        year: 'numeric'
                                      })
                                    : 'N/A'
                                  }
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Activity Stats */}
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-3">Activity Statistics</h3>
                            <div className="grid grid-cols-2 gap-4">
                              <div className="p-4 bg-blue-50 rounded-lg">
                                <p className="text-sm text-gray-600 mb-1">AI Queries</p>
                                <p className="text-2xl font-bold text-blue-600">{selectedUser.ai_queries_count}</p>
                              </div>
                              <div className="p-4 bg-purple-50 rounded-lg">
                                <p className="text-sm text-gray-600 mb-1">Images Generated</p>
                                <p className="text-2xl font-bold text-purple-600">{selectedUser.images_generated}</p>
                              </div>
                            </div>
                          </div>

                          {/* LMS Course Enrollments Section */}
                          <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-lg border-2 border-green-200">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                              <BookOpen className="h-5 w-5 text-green-600" />
                              LMS Course Enrollments
                            </h3>
                            
                            {lmsEnrollmentsLoading ? (
                              <div className="flex items-center justify-center py-4">
                                <RefreshCw className="h-5 w-5 animate-spin text-green-600" />
                                <span className="ml-2 text-gray-600">Loading enrollments...</span>
                              </div>
                            ) : userLmsEnrollments.length > 0 ? (
                              <div className="space-y-3">
                                {userLmsEnrollments.map((enrollment, idx) => (
                                  <div key={idx} className="bg-white p-4 rounded-lg border border-green-200 shadow-sm">
                                    <div className="flex justify-between items-start">
                                      <div>
                                        <p className="font-semibold text-gray-900">{enrollment.course_title}</p>
                                        {enrollment.batch_name && (
                                          <p className="text-sm text-gray-600">Batch: {enrollment.batch_name}</p>
                                        )}
                                        {!enrollment.batch_id && (
                                          <p className="text-sm text-emerald-600">Self-Paced Course</p>
                                        )}
                                        <p className="text-xs text-gray-500 mt-1">
                                          Enrolled: {new Date(enrollment.enrolled_at).toLocaleDateString()}
                                        </p>
                                      </div>
                                      <div className="text-right">
                                        <Badge className={
                                          enrollment.payment_status === 'completed' ? 'bg-green-100 text-green-700' :
                                          enrollment.payment_status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                          enrollment.payment_status === 'free' ? 'bg-blue-100 text-blue-700' :
                                          'bg-gray-100 text-gray-700'
                                        }>
                                          {enrollment.payment_status === 'completed' ? 'Paid' : 
                                           enrollment.payment_status === 'free' ? 'Free' : 
                                           enrollment.payment_status}
                                        </Badge>
                                        <Badge className={`ml-2 ${
                                          enrollment.status === 'active' ? 'bg-green-100 text-green-700' :
                                          enrollment.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                                          enrollment.status === 'dropped' ? 'bg-red-100 text-red-700' :
                                          'bg-gray-100 text-gray-700'
                                        }`}>
                                          {enrollment.status}
                                        </Badge>
                                        {enrollment.progress?.overall_progress_percent > 0 && (
                                          <p className="text-xs text-gray-500 mt-1">
                                            Progress: {enrollment.progress.overall_progress_percent}%
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-gray-500 text-sm py-4 text-center">No LMS course enrollments found for this user.</p>
                            )}
                          </div>

                          {/* Enrollment Section */}
                          <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-6 rounded-lg border-2 border-purple-200">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Enroll User</h3>
                            
                            <div className="space-y-4">
                              {/* Feature Selection */}
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-3">
                                  Select Features & Exams
                                </label>
                                <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                                  {/* All Features */}
                                  <label className="flex items-center space-x-2 p-2 hover:bg-purple-50 rounded">
                                    <input
                                      type="checkbox"
                                      checked={enrollmentFeatures.includes('all')}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setEnrollmentFeatures(['all']);
                                        } else {
                                          setEnrollmentFeatures([]);
                                        }
                                      }}
                                      className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                                    />
                                    <span className="text-sm font-semibold text-purple-700">🎯 All Features (Complete Access)</span>
                                  </label>
                                  
                                  {!enrollmentFeatures.includes('all') && (
                                    <div className="ml-4 space-y-2 border-l-2 border-purple-200 pl-4">
                                      {/* AI Advisor */}
                                      <label className="flex items-center space-x-2 p-2 hover:bg-blue-50 rounded">
                                        <input
                                          type="checkbox"
                                          checked={enrollmentFeatures.includes('ai_advisor')}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setEnrollmentFeatures([...enrollmentFeatures, 'ai_advisor']);
                                            } else {
                                              setEnrollmentFeatures(enrollmentFeatures.filter(f => f !== 'ai_advisor'));
                                            }
                                          }}
                                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                        />
                                        <span className="text-sm text-gray-700">🤖 AI Advisor</span>
                                      </label>
                                      
                                      {/* Learn Research */}
                                      <label className="flex items-center space-x-2 p-2 hover:bg-green-50 rounded">
                                        <input
                                          type="checkbox"
                                          checked={enrollmentFeatures.includes('learn_research')}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setEnrollmentFeatures([...enrollmentFeatures, 'learn_research']);
                                            } else {
                                              setEnrollmentFeatures(enrollmentFeatures.filter(f => f !== 'learn_research'));
                                            }
                                          }}
                                          className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                                        />
                                        <span className="text-sm text-gray-700">📚 Learn Research</span>
                                      </label>
                                      
                                      {/* Exams Divider */}
                                      <div className="pt-2 pb-1">
                                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Medical & Dental Exams</p>
                                      </div>
                                      
                                      {/* Individual Exams - Medical */}
                                      <label className="flex items-center space-x-2 p-2 hover:bg-orange-50 rounded">
                                        <input
                                          type="checkbox"
                                          checked={enrollmentFeatures.includes('exam_USMLE_Step1_Success_Bank')}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setEnrollmentFeatures([...enrollmentFeatures, 'exam_USMLE_Step1_Success_Bank']);
                                            } else {
                                              setEnrollmentFeatures(enrollmentFeatures.filter(f => f !== 'exam_USMLE_Step1_Success_Bank'));
                                            }
                                          }}
                                          className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                                        />
                                        <span className="text-sm text-gray-700">🏥 USMLE Step 1</span>
                                      </label>
                                      
                                      <label className="flex items-center space-x-2 p-2 hover:bg-orange-50 rounded">
                                        <input
                                          type="checkbox"
                                          checked={enrollmentFeatures.includes('exam_USMLE_Step2CK_Success_Bank')}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setEnrollmentFeatures([...enrollmentFeatures, 'exam_USMLE_Step2CK_Success_Bank']);
                                            } else {
                                              setEnrollmentFeatures(enrollmentFeatures.filter(f => f !== 'exam_USMLE_Step2CK_Success_Bank'));
                                            }
                                          }}
                                          className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                                        />
                                        <span className="text-sm text-gray-700">🏥 USMLE Step 2 CK</span>
                                      </label>
                                      
                                      <label className="flex items-center space-x-2 p-2 hover:bg-orange-50 rounded">
                                        <input
                                          type="checkbox"
                                          checked={enrollmentFeatures.includes('exam_USMLE_Step3_Success_Bank')}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setEnrollmentFeatures([...enrollmentFeatures, 'exam_USMLE_Step3_Success_Bank']);
                                            } else {
                                              setEnrollmentFeatures(enrollmentFeatures.filter(f => f !== 'exam_USMLE_Step3_Success_Bank'));
                                            }
                                          }}
                                          className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                                        />
                                        <span className="text-sm text-gray-700">🏥 USMLE Step 3</span>
                                      </label>
                                      
                                      <label className="flex items-center space-x-2 p-2 hover:bg-orange-50 rounded">
                                        <input
                                          type="checkbox"
                                          checked={enrollmentFeatures.includes('exam_AMC_MCQ_Success_Bank')}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setEnrollmentFeatures([...enrollmentFeatures, 'exam_AMC_MCQ_Success_Bank']);
                                            } else {
                                              setEnrollmentFeatures(enrollmentFeatures.filter(f => f !== 'exam_AMC_MCQ_Success_Bank'));
                                            }
                                          }}
                                          className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                                        />
                                        <span className="text-sm text-gray-700">🏥 AMC MCQ</span>
                                      </label>
                                      
                                      <label className="flex items-center space-x-2 p-2 hover:bg-orange-50 rounded">
                                        <input
                                          type="checkbox"
                                          checked={enrollmentFeatures.includes('exam_DHA_MCQ_Success_Bank')}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setEnrollmentFeatures([...enrollmentFeatures, 'exam_DHA_MCQ_Success_Bank']);
                                            } else {
                                              setEnrollmentFeatures(enrollmentFeatures.filter(f => f !== 'exam_DHA_MCQ_Success_Bank'));
                                            }
                                          }}
                                          className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                                        />
                                        <span className="text-sm text-gray-700">🏥 DHA Medical</span>
                                      </label>
                                      
                                      <label className="flex items-center space-x-2 p-2 hover:bg-orange-50 rounded">
                                        <input
                                          type="checkbox"
                                          checked={enrollmentFeatures.includes('exam_MCCQE_PartI_Success_Bank')}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setEnrollmentFeatures([...enrollmentFeatures, 'exam_MCCQE_PartI_Success_Bank']);
                                            } else {
                                              setEnrollmentFeatures(enrollmentFeatures.filter(f => f !== 'exam_MCCQE_PartI_Success_Bank'));
                                            }
                                          }}
                                          className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                                        />
                                        <span className="text-sm text-gray-700">🏥 MCCQE Part I</span>
                                      </label>
                                      
                                      <label className="flex items-center space-x-2 p-2 hover:bg-orange-50 rounded">
                                        <input
                                          type="checkbox"
                                          checked={enrollmentFeatures.includes('exam_PLAB1_Success_Bank')}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setEnrollmentFeatures([...enrollmentFeatures, 'exam_PLAB1_Success_Bank']);
                                            } else {
                                              setEnrollmentFeatures(enrollmentFeatures.filter(f => f !== 'exam_PLAB1_Success_Bank'));
                                            }
                                          }}
                                          className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                                        />
                                        <span className="text-sm text-gray-700">🏥 PLAB1</span>
                                      </label>
                                      
                                      {/* Dental Exams */}
                                      <label className="flex items-center space-x-2 p-2 hover:bg-teal-50 rounded">
                                        <input
                                          type="checkbox"
                                          checked={enrollmentFeatures.includes('exam_DHA_Dental_Success_Bank')}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setEnrollmentFeatures([...enrollmentFeatures, 'exam_DHA_Dental_Success_Bank']);
                                            } else {
                                              setEnrollmentFeatures(enrollmentFeatures.filter(f => f !== 'exam_DHA_Dental_Success_Bank'));
                                            }
                                          }}
                                          className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                                        />
                                        <span className="text-sm text-gray-700">🦷 DHA Dental</span>
                                      </label>
                                      
                                      <label className="flex items-center space-x-2 p-2 hover:bg-teal-50 rounded">
                                        <input
                                          type="checkbox"
                                          checked={enrollmentFeatures.includes('exam_ADC_Part1_Success_Bank')}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setEnrollmentFeatures([...enrollmentFeatures, 'exam_ADC_Part1_Success_Bank']);
                                            } else {
                                              setEnrollmentFeatures(enrollmentFeatures.filter(f => f !== 'exam_ADC_Part1_Success_Bank'));
                                            }
                                          }}
                                          className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                                        />
                                        <span className="text-sm text-gray-700">🦷 ADC Part 1</span>
                                      </label>
                                      
                                      <label className="flex items-center space-x-2 p-2 hover:bg-teal-50 rounded">
                                        <input
                                          type="checkbox"
                                          checked={enrollmentFeatures.includes('exam_ORE_Part1_Success_Bank')}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setEnrollmentFeatures([...enrollmentFeatures, 'exam_ORE_Part1_Success_Bank']);
                                            } else {
                                              setEnrollmentFeatures(enrollmentFeatures.filter(f => f !== 'exam_ORE_Part1_Success_Bank'));
                                            }
                                          }}
                                          className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                                        />
                                        <span className="text-sm text-gray-700">🦷 ORE Part 1</span>
                                      </label>
                                      
                                      <label className="flex items-center space-x-2 p-2 hover:bg-teal-50 rounded">
                                        <input
                                          type="checkbox"
                                          checked={enrollmentFeatures.includes('exam_INBDE_Success_Bank')}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setEnrollmentFeatures([...enrollmentFeatures, 'exam_INBDE_Success_Bank']);
                                            } else {
                                              setEnrollmentFeatures(enrollmentFeatures.filter(f => f !== 'exam_INBDE_Success_Bank'));
                                            }
                                          }}
                                          className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                                        />
                                        <span className="text-sm text-gray-700">🦷 INBDE</span>
                                      </label>
                                      
                                      {/* Pharmacy Exams Divider */}
                                      <div className="pt-2 pb-1">
                                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Pharmacy Exams</p>
                                      </div>
                                      
                                      <label className="flex items-center space-x-2 p-2 hover:bg-green-50 rounded">
                                        <input
                                          type="checkbox"
                                          checked={enrollmentFeatures.includes('exam_NAPLEX_Success_Bank')}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setEnrollmentFeatures([...enrollmentFeatures, 'exam_NAPLEX_Success_Bank']);
                                            } else {
                                              setEnrollmentFeatures(enrollmentFeatures.filter(f => f !== 'exam_NAPLEX_Success_Bank'));
                                            }
                                          }}
                                          className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                                        />
                                        <span className="text-sm text-gray-700">💊 NAPLEX</span>
                                      </label>
                                      
                                      <label className="flex items-center space-x-2 p-2 hover:bg-green-50 rounded">
                                        <input
                                          type="checkbox"
                                          checked={enrollmentFeatures.includes('exam_MPJE_Success_Bank')}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setEnrollmentFeatures([...enrollmentFeatures, 'exam_MPJE_Success_Bank']);
                                            } else {
                                              setEnrollmentFeatures(enrollmentFeatures.filter(f => f !== 'exam_MPJE_Success_Bank'));
                                            }
                                          }}
                                          className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                                        />
                                        <span className="text-sm text-gray-700">💊 MPJE</span>
                                      </label>
                                      
                                      <label className="flex items-center space-x-2 p-2 hover:bg-green-50 rounded">
                                        <input
                                          type="checkbox"
                                          checked={enrollmentFeatures.includes('exam_PEBC_Part1_Success_Bank')}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setEnrollmentFeatures([...enrollmentFeatures, 'exam_PEBC_Part1_Success_Bank']);
                                            } else {
                                              setEnrollmentFeatures(enrollmentFeatures.filter(f => f !== 'exam_PEBC_Part1_Success_Bank'));
                                            }
                                          }}
                                          className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                                        />
                                        <span className="text-sm text-gray-700">💊 PEBC Part 1</span>
                                      </label>
                                      
                                      <label className="flex items-center space-x-2 p-2 hover:bg-green-50 rounded">
                                        <input
                                          type="checkbox"
                                          checked={enrollmentFeatures.includes('exam_GPhC_Paper1_Calculations_Success_Bank')}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setEnrollmentFeatures([...enrollmentFeatures, 'exam_GPhC_Paper1_Calculations_Success_Bank']);
                                            } else {
                                              setEnrollmentFeatures(enrollmentFeatures.filter(f => f !== 'exam_GPhC_Paper1_Calculations_Success_Bank'));
                                            }
                                          }}
                                          className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                                        />
                                        <span className="text-sm text-gray-700">💊 GPhC Paper 1</span>
                                      </label>
                                      
                                      <label className="flex items-center space-x-2 p-2 hover:bg-green-50 rounded">
                                        <input
                                          type="checkbox"
                                          checked={enrollmentFeatures.includes('exam_GPhC_Paper2_Clinical_Success_Bank')}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setEnrollmentFeatures([...enrollmentFeatures, 'exam_GPhC_Paper2_Clinical_Success_Bank']);
                                            } else {
                                              setEnrollmentFeatures(enrollmentFeatures.filter(f => f !== 'exam_GPhC_Paper2_Clinical_Success_Bank'));
                                            }
                                          }}
                                          className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                                        />
                                        <span className="text-sm text-gray-700">💊 GPhC Paper 2</span>
                                      </label>
                                      
                                      <label className="flex items-center space-x-2 p-2 hover:bg-green-50 rounded">
                                        <input
                                          type="checkbox"
                                          checked={enrollmentFeatures.includes('exam_Australia_OPRA_Success_Bank')}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setEnrollmentFeatures([...enrollmentFeatures, 'exam_Australia_OPRA_Success_Bank']);
                                            } else {
                                              setEnrollmentFeatures(enrollmentFeatures.filter(f => f !== 'exam_Australia_OPRA_Success_Bank'));
                                            }
                                          }}
                                          className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                                        />
                                        <span className="text-sm text-gray-700">💊 Australia OPRA</span>
                                      </label>
                                      
                                      <label className="flex items-center space-x-2 p-2 hover:bg-green-50 rounded">
                                        <input
                                          type="checkbox"
                                          checked={enrollmentFeatures.includes('exam_NewZealand_CAP_Success_Bank')}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setEnrollmentFeatures([...enrollmentFeatures, 'exam_NewZealand_CAP_Success_Bank']);
                                            } else {
                                              setEnrollmentFeatures(enrollmentFeatures.filter(f => f !== 'exam_NewZealand_CAP_Success_Bank'));
                                            }
                                          }}
                                          className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                                        />
                                        <span className="text-sm text-gray-700">💊 New Zealand CAP</span>
                                      </label>
                                      
                                      <label className="flex items-center space-x-2 p-2 hover:bg-green-50 rounded">
                                        <input
                                          type="checkbox"
                                          checked={enrollmentFeatures.includes('exam_DHA_Pharma_Success_Bank')}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setEnrollmentFeatures([...enrollmentFeatures, 'exam_DHA_Pharma_Success_Bank']);
                                            } else {
                                              setEnrollmentFeatures(enrollmentFeatures.filter(f => f !== 'exam_DHA_Pharma_Success_Bank'));
                                            }
                                          }}
                                          className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                                        />
                                        <span className="text-sm text-gray-700">💊 DHA Pharmacy</span>
                                      </label>
                                      
                                      <label className="flex items-center space-x-2 p-2 hover:bg-green-50 rounded">
                                        <input
                                          type="checkbox"
                                          checked={enrollmentFeatures.includes('exam_SPLE_Success_Bank')}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setEnrollmentFeatures([...enrollmentFeatures, 'exam_SPLE_Success_Bank']);
                                            } else {
                                              setEnrollmentFeatures(enrollmentFeatures.filter(f => f !== 'exam_SPLE_Success_Bank'));
                                            }
                                          }}
                                          className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                                        />
                                        <span className="text-sm text-gray-700">💊 SPLE</span>
                                      </label>
                                      
                                      {/* Engineering Exams Divider */}
                                      <div className="pt-2 pb-1">
                                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Engineering Exams</p>
                                      </div>
                                      
                                      <label className="flex items-center space-x-2 p-2 hover:bg-indigo-50 rounded">
                                        <input
                                          type="checkbox"
                                          checked={enrollmentFeatures.includes('exam_IIT_JEE_Success_Bank')}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setEnrollmentFeatures([...enrollmentFeatures, 'exam_IIT_JEE_Success_Bank']);
                                            } else {
                                              setEnrollmentFeatures(enrollmentFeatures.filter(f => f !== 'exam_IIT_JEE_Success_Bank'));
                                            }
                                          }}
                                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                        />
                                        <span className="text-sm text-gray-700">⚙️ IIT-JEE</span>
                                      </label>
                                      
                                      <label className="flex items-center space-x-2 p-2 hover:bg-indigo-50 rounded">
                                        <input
                                          type="checkbox"
                                          checked={enrollmentFeatures.includes('exam_NEET_UG_Success_Bank')}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setEnrollmentFeatures([...enrollmentFeatures, 'exam_NEET_UG_Success_Bank']);
                                            } else {
                                              setEnrollmentFeatures(enrollmentFeatures.filter(f => f !== 'exam_NEET_UG_Success_Bank'));
                                            }
                                          }}
                                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                        />
                                        <span className="text-sm text-gray-700">⚙️ NEET-UG</span>
                                      </label>
                                      
                                      <label className="flex items-center space-x-2 p-2 hover:bg-indigo-50 rounded">
                                        <input
                                          type="checkbox"
                                          checked={enrollmentFeatures.includes('exam_GRE_Success_Bank')}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setEnrollmentFeatures([...enrollmentFeatures, 'exam_GRE_Success_Bank']);
                                            } else {
                                              setEnrollmentFeatures(enrollmentFeatures.filter(f => f !== 'exam_GRE_Success_Bank'));
                                            }
                                          }}
                                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                        />
                                        <span className="text-sm text-gray-700">🌍 GRE</span>
                                      </label>
                                      
                                      <label className="flex items-center space-x-2 p-2 hover:bg-indigo-50 rounded">
                                        <input
                                          type="checkbox"
                                          checked={enrollmentFeatures.includes('exam_GMAT_Success_Bank')}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setEnrollmentFeatures([...enrollmentFeatures, 'exam_GMAT_Success_Bank']);
                                            } else {
                                              setEnrollmentFeatures(enrollmentFeatures.filter(f => f !== 'exam_GMAT_Success_Bank'));
                                            }
                                          }}
                                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                        />
                                        <span className="text-sm text-gray-700">🎯 GMAT</span>
                                      </label>

                                      {/* JNTU Special Plans */}
                                      <div className="mt-4 pt-4 border-t border-gray-200">
                                        <p className="text-xs font-semibold text-purple-600 mb-2 uppercase">JNTU Student Success Plans</p>
                                        
                                        {/* Engineering Student Success */}
                                        <label className="flex items-center space-x-2 p-2 hover:bg-blue-50 rounded">
                                          <input
                                            type="checkbox"
                                            checked={enrollmentFeatures.includes('engineering-student-success')}
                                            onChange={(e) => {
                                              if (e.target.checked) {
                                                setEnrollmentFeatures([...enrollmentFeatures, 'engineering-student-success']);
                                              } else {
                                                setEnrollmentFeatures(enrollmentFeatures.filter(f => f !== 'engineering-student-success'));
                                              }
                                            }}
                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                          />
                                          <span className="text-sm text-gray-700">⚙️ Engineering Student Success Plan</span>
                                        </label>

                                        {/* Business & Finance Student Success */}
                                        <label className="flex items-center space-x-2 p-2 hover:bg-amber-50 rounded">
                                          <input
                                            type="checkbox"
                                            checked={enrollmentFeatures.includes('business-finance-student-success')}
                                            onChange={(e) => {
                                              if (e.target.checked) {
                                                setEnrollmentFeatures([...enrollmentFeatures, 'business-finance-student-success']);
                                              } else {
                                                setEnrollmentFeatures(enrollmentFeatures.filter(f => f !== 'business-finance-student-success'));
                                              }
                                            }}
                                            className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
                                          />
                                          <span className="text-sm text-gray-700">💼 Business & Finance Student Success Plan</span>
                                        </label>

                                        {/* Pharma Student Success */}
                                        <label className="flex items-center space-x-2 p-2 hover:bg-teal-50 rounded">
                                          <input
                                            type="checkbox"
                                            checked={enrollmentFeatures.includes('pharma-student-success')}
                                            onChange={(e) => {
                                              if (e.target.checked) {
                                                setEnrollmentFeatures([...enrollmentFeatures, 'pharma-student-success']);
                                              } else {
                                                setEnrollmentFeatures(enrollmentFeatures.filter(f => f !== 'pharma-student-success'));
                                              }
                                            }}
                                            className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                                          />
                                          <span className="text-sm text-gray-700">💊 Pharma Student Success Plan</span>
                                        </label>

                                        {/* Life-Sciences Student Success */}
                                        <label className="flex items-center space-x-2 p-2 hover:bg-emerald-50 rounded">
                                          <input
                                            type="checkbox"
                                            checked={enrollmentFeatures.includes('lifesciences-student-success')}
                                            onChange={(e) => {
                                              if (e.target.checked) {
                                                setEnrollmentFeatures([...enrollmentFeatures, 'lifesciences-student-success']);
                                              } else {
                                                setEnrollmentFeatures(enrollmentFeatures.filter(f => f !== 'lifesciences-student-success'));
                                              }
                                            }}
                                            className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                                          />
                                          <span className="text-sm text-gray-700">🧬 Life-Sciences Student Success Plan</span>
                                        </label>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Days Input */}
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Number of Days
                                </label>
                                <input
                                  type="number"
                                  min="1"
                                  value={enrollmentDays}
                                  onChange={(e) => setEnrollmentDays(parseInt(e.target.value) || 1)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                  placeholder="30"
                                />
                              </div>

                              {/* Enroll Button */}
                              <button
                                onClick={handleEnrollUser}
                                disabled={enrolling || enrollmentFeatures.length === 0}
                                className="w-full px-4 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
                              >
                                {enrolling ? 'Enrolling...' : 'Enroll User'}
                              </button>
                            </div>
                          </div>


                          {/* Grant Nerd-AI Credits Section */}
                          <div className="bg-gradient-to-br from-amber-50 to-yellow-50 p-6 rounded-lg border-2 border-amber-200 shadow-sm">
                            <h3 className="text-lg font-semibold text-amber-900 mb-4 flex items-center gap-2">
                              <svg className="w-5 h-5 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                                <circle cx="10" cy="10" r="9" fill="currentColor" opacity="0.3"/>
                              </svg>
                              <svg className="w-3 h-3 text-yellow-500 absolute ml-1" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                              </svg>
                              Grant Nerd-AI Credits
                            </h3>
                            
                            <div className="bg-white p-4 rounded-lg border border-amber-200">
                              <div className="mb-4">
                                <p className="text-sm text-gray-700 mb-2">
                                  Current Balance: <span className="font-bold text-amber-900">{selectedUser.nerd_ai_credits || 0} credits</span>
                                </p>
                                {selectedUser.credits_expiry && (
                                  <p className="text-xs text-gray-500">
                                    Expires: {new Date(selectedUser.credits_expiry).toLocaleDateString()}
                                  </p>
                                )}
                              </div>
                              
                              <div className="grid grid-cols-2 gap-3 mb-3">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Credits to Add</label>
                                  <input
                                    type="number"
                                    min="0"
                                    value={creditAmount}
                                    onChange={(e) => setCreditAmount(parseInt(e.target.value) || 0)}
                                    className="w-full px-3 py-2 border border-amber-300 rounded-md focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                    placeholder="e.g., 1500"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Validity (Days)</label>
                                  <input
                                    type="number"
                                    min="1"
                                    value={creditValidityDays}
                                    onChange={(e) => setCreditValidityDays(parseInt(e.target.value) || 30)}
                                    className="w-full px-3 py-2 border border-amber-300 rounded-md focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                    placeholder="e.g., 30"
                                  />
                                </div>
                              </div>
                              
                              <button
                                onClick={handleGrantCredits}
                                disabled={grantingCredits || !creditAmount || creditAmount <= 0}
                                className="w-full new-badge-gold-shine text-white font-semibold py-3 px-4 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-all border-0"
                              >
                                {grantingCredits ? 'Granting Credits...' : `Grant ${creditAmount || 0} Credits`}
                              </button>
                            </div>
                          </div>

                          {/* Payment History & Courses Enrolled */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Payment History Card */}
                            <div>
                              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                <TrendingUp className="h-5 w-5 text-green-600" />
                                Payment History ({selectedUser.total_payments || 0})
                              </h3>
                              <div className="border rounded-lg p-4 bg-gray-50 max-h-96 overflow-y-auto">
                                {selectedUser.payment_history && selectedUser.payment_history.length > 0 ? (
                                  <div className="space-y-3">
                                    {selectedUser.payment_history.map((payment, index) => {
                                      const isPaid = payment.status === 'completed' || payment.status === 'paid';
                                      return (
                                      <div key={index} className={`bg-white p-3 rounded-lg border-l-4 ${isPaid ? 'border-green-500' : 'border-red-500'}`}>
                                        <div className="flex justify-between items-start gap-3">
                                          <div className="flex-1">
                                            <p className="font-semibold text-gray-900">
                                              {payment.type === 'event' ? `Event: ${payment.event_title || payment.plan_name || 'Untitled'}` : payment.plan_name}
                                            </p>
                                            <p className="text-xs text-gray-600 mt-1">
                                              Duration: {payment.plan_duration || (payment.type === 'event' ? 'Event Registration' : '-')}
                                            </p>
                                            <p className="text-xs text-gray-600">
                                              Payment ID: {payment.payment_id}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                              {payment.completed_at ? new Date(payment.completed_at).toLocaleDateString('en-US', {
                                                month: 'short',
                                                day: 'numeric',
                                                year: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                              }) : '-'}
                                            </p>
                                          </div>
                                          <div className="text-right">
                                            <p className={`font-bold ${isPaid ? 'text-green-600' : 'text-red-600'}`}>
                                              ₹{(() => {
                                                if (payment.total_amount) {
                                                  return payment.total_amount.toFixed(2);
                                                }
                                                // For old records: if amount < 1000, it's likely in rupees already
                                                // If amount >= 1000, it's in paise, divide by 100
                                                const amount = payment.amount || 0;
                                                return amount >= 1000 ? (amount / 100).toFixed(2) : amount.toFixed(2);
                                              })()} INR
                                            </p>
                                            <Badge className={`mt-1 ${isPaid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                              {isPaid ? 'Completed' : 'Unsuccessful'}
                                            </Badge>
                                            <div className="flex flex-col gap-1 mt-2">
                                              <button
                                                onClick={() => downloadInvoice(payment.order_id, selectedUser.id, 'html')}
                                                className="text-xs bg-blue-500 hover:bg-blue-700 text-white px-3 py-1 rounded"
                                              >
                                                HTML
                                              </button>
                                              <button
                                                onClick={() => downloadInvoice(payment.order_id, selectedUser.id, 'pdf')}
                                                className="text-xs bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded"
                                              >
                                                PDF
                                              </button>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  <p className="text-sm text-gray-600 text-center py-4">
                                    No payment history found.
                                  </p>
                                )}
                              </div>
                            </div>

                            {/* Courses Enrolled Card */}
                            <div>
                              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                <BookOpen className="h-5 w-5 text-blue-600" />
                                Courses Enrolled
                              </h3>
                              <div className="border rounded-lg p-4 bg-gray-50 max-h-96 overflow-y-auto">
                                {selectedUser.enrolled_features && selectedUser.enrolled_features.length > 0 ? (
                                  <div className="space-y-3">
                                    {/* Enrollment Status */}
                                    <div className="bg-white p-3 rounded-lg border-l-4 border-green-500">
                                      <div className="flex justify-between items-start">
                                        <div>
                                          <p className="font-semibold text-gray-900">
                                            {selectedUser.is_enrolled ? '✅ Active Enrollment' : '⚠️ Expired Enrollment'}
                                          </p>
                                          <p className="text-xs text-gray-600 mt-1">
                                            {selectedUser.enrollment_expiry 
                                              ? `Expires: ${new Date(selectedUser.enrollment_expiry).toLocaleDateString('en-US', {
                                                  month: 'long',
                                                  day: 'numeric',
                                                  year: 'numeric'
                                                })}`
                                              : 'No expiry date'
                                            }
                                          </p>
                                        </div>
                                        <Badge className={selectedUser.is_enrolled ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                                          {selectedUser.is_enrolled ? 'Active' : 'Expired'}
                                        </Badge>
                                      </div>
                                    </div>

                                    {/* Crisis Support Alert */}
                                    {selectedUser.crisis_flagged && (
                                      <div className="bg-red-50 p-3 rounded-lg border-l-4 border-red-500">
                                        <div className="flex justify-between items-start">
                                          <div>
                                            <p className="font-semibold text-red-900">
                                              ⚠️ User Needs Support - Crisis Flagged
                                            </p>
                                            <p className="text-xs text-red-600 mt-1">
                                              Flagged on: {selectedUser.crisis_flagged_at 
                                                ? new Date(selectedUser.crisis_flagged_at).toLocaleDateString('en-US', {
                                                    month: 'long',
                                                    day: 'numeric',
                                                    year: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                  })
                                                : 'Unknown'
                                              }
                                            </p>
                                            {selectedUser.crisis_queries && selectedUser.crisis_queries.length > 0 && (
                                              <div className="mt-2">
                                                <p className="text-xs font-medium text-red-700">Recent concerning queries:</p>
                                                <ul className="text-xs text-red-600 mt-1 space-y-1">
                                                  {selectedUser.crisis_queries.slice(-3).map((q, idx) => (
                                                    <li key={idx} className="bg-red-100 p-1 rounded">
                                                      "{q.query?.substring(0, 100)}..."
                                                    </li>
                                                  ))}
                                                </ul>
                                              </div>
                                            )}
                                            <p className="text-xs text-red-500 mt-2 italic">
                                              Please consider reaching out to this user for support
                                            </p>
                                            <button
                                              onClick={async () => {
                                                try {
                                                  const token = localStorage.getItem('token');
                                                  await axios.post(
                                                    `${API}/admin/clear-crisis-flag/${selectedUser.id}`,
                                                    {},
                                                    { headers: { 'Authorization': `Bearer ${token}` } }
                                                  );
                                                  toast.success('Crisis flag cleared');
                                                  fetchUsers();
                                                  setSelectedUser({...selectedUser, crisis_flagged: false});
                                                } catch (error) {
                                                  toast.error('Failed to clear flag');
                                                }
                                              }}
                                              className="mt-2 px-3 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600 transition-colors"
                                            >
                                              ✓ Mark as Addressed
                                            </button>
                                          </div>
                                          <Badge className="bg-red-500 text-white animate-pulse">
                                            Needs Attention
                                          </Badge>
                                        </div>
                                      </div>
                                    )}

                                    {/* AI Buddy Status */}
                                    {selectedUser.ai_buddy_expiry && (
                                      <div className="bg-white p-3 rounded-lg border-l-4 border-purple-500">
                                        <div className="flex justify-between items-start">
                                          <div>
                                            <p className="font-semibold text-gray-900">
                                              {selectedUser.has_ai_buddy ? '🤖 AI Buddy Active' : '⏰ AI Buddy Expired'}
                                            </p>
                                            <p className="text-xs text-gray-600 mt-1">
                                              {`Expires: ${new Date(selectedUser.ai_buddy_expiry).toLocaleDateString('en-US', {
                                                month: 'long',
                                                day: 'numeric',
                                                year: 'numeric'
                                              })}`}
                                            </p>
                                            <p className="text-xs text-gray-500 mt-1">
                                              Includes: AI Advisor + Ask Nerd-AI
                                            </p>
                                          </div>
                                          <Badge className={selectedUser.has_ai_buddy ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700'}>
                                            {selectedUser.has_ai_buddy ? 'Active' : 'Expired'}
                                          </Badge>
                                        </div>
                                      </div>
                                    )}

                                    {/* Enrolled Features List */}
                                    <div>
                                      <p className="text-sm font-semibold text-gray-700 mb-2">Enrolled Features:</p>
                                      <div className="space-y-2">
                                        {selectedUser.enrolled_features.includes('all') ? (
                                          <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                                            <div className="flex items-center gap-2">
                                              <span className="text-lg">🎯</span>
                                              <div>
                                                <p className="font-medium text-purple-900">All Features</p>
                                                <p className="text-xs text-purple-700">Complete access to entire platform</p>
                                              </div>
                                            </div>
                                          </div>
                                        ) : (
                                          <>
                                            {selectedUser.enrolled_features.map((feature, idx) => {
                                              // Format feature name
                                              let displayName = '';
                                              let icon = '📦';
                                              let bgColor = 'bg-blue-50';
                                              let borderColor = 'border-blue-200';
                                              let textColor = 'text-blue-900';
                                              
                                              if (feature === 'ai_advisor') {
                                                displayName = 'AI Advisor';
                                                icon = '🤖';
                                                bgColor = 'bg-blue-50';
                                                borderColor = 'border-blue-200';
                                                textColor = 'text-blue-900';
                                              } else if (feature === 'learn_research') {
                                                displayName = 'Learn Research';
                                                icon = '📚';
                                                bgColor = 'bg-green-50';
                                                borderColor = 'border-green-200';
                                                textColor = 'text-green-900';
                                              } else if (feature.startsWith('exam_')) {
                                                const examName = feature.replace('exam_', '').replace(/_/g, ' ').replace('Success Bank', '');
                                                displayName = examName;
                                                
                                                if (examName.includes('IIT') || examName.includes('NEET')) {
                                                  icon = '⚙️';
                                                  bgColor = 'bg-indigo-50';
                                                  borderColor = 'border-indigo-200';
                                                  textColor = 'text-indigo-900';
                                                } else if (examName.includes('Dental') || examName.includes('ADC') || examName.includes('ORE') || examName.includes('INBDE')) {
                                                  icon = '🦷';
                                                  bgColor = 'bg-teal-50';
                                                  borderColor = 'border-teal-200';
                                                  textColor = 'text-teal-900';
                                                } else {
                                                  icon = '🏥';
                                                  bgColor = 'bg-orange-50';
                                                  borderColor = 'border-orange-200';
                                                  textColor = 'text-orange-900';
                                                }
                                              }
                                              
                                              return (
                                                <div key={idx} className={`${bgColor} p-3 rounded-lg border ${borderColor}`}>
                                                  <div className="flex items-center gap-2">
                                                    <span className="text-lg">{icon}</span>
                                                    <p className={`font-medium ${textColor}`}>{displayName}</p>
                                                  </div>
                                                </div>
                                              );
                                            })}
                                          </>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="text-center py-6">
                                    <div className="text-4xl mb-2">📚</div>
                                    <p className="text-sm text-gray-600">No courses enrolled yet</p>
                                    <p className="text-xs text-gray-500 mt-1">Use the "Enroll User" section above to grant access</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </>
        )}
        
        {/* Employer Management Section */}
        <div className="mt-8">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="h-6 w-6 text-blue-600" />
                Employer Management
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Search Bar & Filter */}
              <div className="mb-6 flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search employers by name, email, or company..."
                    value={employerSearchQuery}
                    onChange={(e) => {
                      setEmployerSearchQuery(e.target.value);
                      setEmployerCurrentPage(1);
                    }}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                {/* Status Filter */}
                <select
                  value={employerStatusFilter}
                  onChange={(e) => {
                    setEmployerStatusFilter(e.target.value);
                    setEmployerCurrentPage(1);
                  }}
                  className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>

              {/* Employer List Table */}
              {employersLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : employers.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Briefcase className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                  <p>No employers found</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="text-left p-3 font-semibold text-gray-700">Name</th>
                          <th className="text-left p-3 font-semibold text-gray-700">Email</th>
                          <th className="text-left p-3 font-semibold text-gray-700">Company</th>
                          <th className="text-left p-3 font-semibold text-gray-700">Job Title</th>
                          <th className="text-left p-3 font-semibold text-gray-700">Jobs Posted</th>
                          <th className="text-left p-3 font-semibold text-gray-700">Status</th>
                          <th className="text-left p-3 font-semibold text-gray-700">Joined</th>
                          <th className="text-left p-3 font-semibold text-gray-700">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {employers.map((employer) => (
                          <tr key={employer.id} className="border-b hover:bg-gray-50">
                            <td className="p-3">
                              <div className="flex items-center gap-2">
                                {employer.avatar_url ? (
                                  <img 
                                    src={employer.avatar_url} 
                                    alt={employer.full_name}
                                    className="h-8 w-8 rounded-full object-cover" loading="lazy" decoding="async"/>
                                ) : (
                                  <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                                    <span className="text-sm font-medium text-blue-600">
                                      {employer.full_name.charAt(0).toUpperCase()}
                                    </span>
                                  </div>
                                )}
                                <span className="font-medium text-gray-900">{employer.full_name}</span>
                              </div>
                            </td>
                            <td className="p-3 text-gray-600">{employer.email}</td>
                            <td className="p-3 text-gray-600">{employer.company_name}</td>
                            <td className="p-3 text-gray-600">{employer.job_title}</td>
                            <td className="p-3 text-gray-600 text-center">{employer.jobs_posted}</td>
                            <td className="p-3">
                              <Badge 
                                className={
                                  employer.employer_status === 'approved'
                                    ? 'bg-green-100 text-green-700' 
                                    : employer.employer_status === 'rejected'
                                    ? 'bg-red-100 text-red-700'
                                    : 'bg-yellow-100 text-yellow-700'
                                }
                              >
                                {employer.employer_status === 'approved' ? '✓ Approved' : 
                                 employer.employer_status === 'rejected' ? '✗ Rejected' : 
                                 '⏳ Pending'}
                              </Badge>
                            </td>
                            <td className="p-3 text-gray-600">
                              {employer.created_at 
                                ? new Date(employer.created_at).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric'
                                  })
                                : 'N/A'
                              }
                            </td>
                            <td className="p-3">
                              <button 
                                onClick={() => setSelectedEmployer(employer)}
                                className="text-blue-600 hover:underline text-sm font-medium"
                              >
                                Manage
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {employerTotalPages > 1 && (
                    <div className="mt-6 flex items-center justify-between">
                      <p className="text-sm text-gray-600">
                        Page {employerCurrentPage} of {employerTotalPages}
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setEmployerCurrentPage(prev => Math.max(1, prev - 1))}
                          disabled={employerCurrentPage === 1}
                          className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                        >
                          <ChevronLeft className="h-4 w-4" />
                          Previous
                        </button>
                        <button
                          onClick={() => setEmployerCurrentPage(prev => Math.min(employerTotalPages, prev + 1))}
                          disabled={employerCurrentPage === employerTotalPages}
                          className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                        >
                          Next
                          <ChevronRight className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Employer Details Modal */}
              {selectedEmployer && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                  <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                    <div className="p-6 border-b sticky top-0 bg-white">
                      <div className="flex justify-between items-start">
                        <div className="flex items-center gap-3">
                          {selectedEmployer.avatar_url ? (
                            <img 
                              src={selectedEmployer.avatar_url} 
                              alt={selectedEmployer.full_name}
                              className="h-16 w-16 rounded-full object-cover" loading="lazy" decoding="async"/>
                          ) : (
                            <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center">
                              <span className="text-2xl font-medium text-blue-600">
                                {selectedEmployer.full_name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                          )}
                          <div>
                            <h2 className="text-2xl font-bold text-gray-900">{selectedEmployer.full_name}</h2>
                            <p className="text-gray-600">{selectedEmployer.email}</p>
                          </div>
                        </div>
                        <button 
                          onClick={() => setSelectedEmployer(null)}
                          className="text-gray-500 hover:text-gray-700 text-2xl"
                        >
                          ×
                        </button>
                      </div>
                    </div>

                    <div className="p-6 space-y-6">
                      {/* Employer Info */}
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">Employer Information</h3>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-sm text-gray-600">Company Name</p>
                            <p className="font-medium">{selectedEmployer.company_name}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Job Title</p>
                            <p className="font-medium">{selectedEmployer.job_title}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Phone Number</p>
                            <p className="font-medium">{selectedEmployer.phone}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Jobs Posted</p>
                            <p className="font-medium">{selectedEmployer.jobs_posted}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Joined Date</p>
                            <p className="font-medium">
                              {selectedEmployer.created_at 
                                ? new Date(selectedEmployer.created_at).toLocaleDateString('en-US', {
                                    month: 'long',
                                    day: 'numeric',
                                    year: 'numeric'
                                  })
                                : 'N/A'
                              }
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Account Status</p>
                            <Badge 
                              className={
                                selectedEmployer.is_active 
                                  ? 'bg-green-100 text-green-700' 
                                  : 'bg-red-100 text-red-700'
                              }
                            >
                              {selectedEmployer.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </div>
                        </div>
                      </div>

                      {/* Approval Status */}
                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border-2 border-blue-200">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Employer Approval Status</h3>
                        
                        <div className="mb-4">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="text-sm font-medium text-gray-700">Current Status:</span>
                            <Badge 
                              className={`text-base px-4 py-1 ${
                                selectedEmployer.employer_status === 'approved'
                                  ? 'bg-green-100 text-green-700' 
                                  : selectedEmployer.employer_status === 'rejected'
                                  ? 'bg-red-100 text-red-700'
                                  : 'bg-yellow-100 text-yellow-700'
                              }`}
                            >
                              {selectedEmployer.employer_status === 'approved' ? '✓ Approved' : 
                               selectedEmployer.employer_status === 'rejected' ? '✗ Rejected' : 
                               '⏳ Pending Approval'}
                            </Badge>
                          </div>
                          
                          {selectedEmployer.employer_status_updated_at && (
                            <p className="text-xs text-gray-500">
                              Last updated: {new Date(selectedEmployer.employer_status_updated_at).toLocaleString()}
                              {selectedEmployer.employer_approved_by && ` by ${selectedEmployer.employer_approved_by}`}
                            </p>
                          )}
                        </div>

                        <div className="flex gap-3">
                          <button
                            onClick={() => handleApproveEmployer(selectedEmployer.id)}
                            disabled={approvingEmployer || selectedEmployer.employer_status === 'approved'}
                            className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
                          >
                            {approvingEmployer ? 'Processing...' : 
                             selectedEmployer.employer_status === 'approved' ? '✓ Already Approved' : 
                             '✓ Approve Employer'}
                          </button>
                          
                          <button
                            onClick={() => handleRejectEmployer(selectedEmployer.id)}
                            disabled={approvingEmployer || selectedEmployer.employer_status === 'rejected'}
                            className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
                          >
                            {approvingEmployer ? 'Processing...' : 
                             selectedEmployer.employer_status === 'rejected' ? '✗ Already Rejected' : 
                             '✗ Reject Employer'}
                          </button>
                        </div>

                        <div className="mt-4 p-3 bg-blue-100 rounded-lg">
                          <p className="text-xs text-blue-800">
                            <strong>Note:</strong> Approved employers can access the Employer Dashboard and post jobs. 
                            Rejected employers will not see the Employer Dashboard option.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        
        {/* Need Support Section */}
        <div className="mt-8">
          <Card className="border-red-200 bg-red-50/30">
            <CardHeader className="bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-t-lg">
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="h-6 w-6" />
                Need Support ({supportUsers.length})
                {supportUsers.length > 0 && (
                  <span className="ml-2 px-2 py-1 bg-white text-red-600 text-xs font-bold rounded-full animate-pulse">
                    Action Required
                  </span>
                )}
              </CardTitle>
              <p className="text-red-100 text-sm mt-1">Users who may need emotional support or counseling</p>
            </CardHeader>
            <CardContent className="p-6">
              {supportUsersLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
                </div>
              ) : supportUsers.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <div className="bg-green-100 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3">
                    <span className="text-3xl">✓</span>
                  </div>
                  <p className="text-green-600 font-medium">No users need support at this time</p>
                  <p className="text-sm text-gray-400 mt-1">Great job keeping everyone happy!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {supportUsers.map((user) => (
                    <div 
                      key={user.id} 
                      className="bg-white border border-red-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          {user.avatar_url ? (
                            <img 
                              src={user.avatar_url} 
                              alt={user.full_name}
                              className="h-12 w-12 rounded-full object-cover border-2 border-red-300" loading="lazy" decoding="async"/>
                          ) : (
                            <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center border-2 border-red-300">
                              <span className="text-lg font-medium text-red-600">
                                {user.full_name?.charAt(0)?.toUpperCase() || '?'}
                              </span>
                            </div>
                          )}
                          <div>
                            <p className="font-semibold text-gray-900">{user.full_name}</p>
                            <p className="text-sm text-gray-600">{user.email}</p>
                            <p className="text-xs text-gray-500">{user.phone || 'No phone'}</p>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <Badge className="bg-red-500 text-white animate-pulse">
                            ⚠️ Needs Support
                          </Badge>
                          {user.crisis_flagged_at && (
                            <p className="text-xs text-gray-500">
                              Flagged: {new Date(user.crisis_flagged_at).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      {/* Crisis Queries */}
                      {user.crisis_queries && user.crisis_queries.length > 0 && (
                        <div className="mt-4 bg-red-50 rounded-lg p-3">
                          <p className="text-xs font-semibold text-red-700 mb-2">Concerning Messages:</p>
                          <div className="space-y-2">
                            {user.crisis_queries.slice(-3).map((q, idx) => (
                              <div key={idx} className="bg-white p-2 rounded border border-red-100">
                                <p className="text-sm text-gray-700">"{q.query?.substring(0, 150)}{q.query?.length > 150 ? '...' : ''}"</p>
                                <p className="text-xs text-gray-400 mt-1">
                                  {q.timestamp ? new Date(q.timestamp).toLocaleString() : ''}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Action Buttons */}
                      <div className="mt-4 flex gap-2">
                        <button
                          onClick={() => handleClearCrisisFlag(user.id)}
                          className="px-4 py-2 bg-green-500 text-white text-sm font-medium rounded-lg hover:bg-green-600 transition-colors"
                        >
                          ✓ Mark as Addressed
                        </button>
                        <button
                          onClick={() => setSelectedUser(user)}
                          className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors"
                        >
                          View Full Profile
                        </button>
                        {user.email && (
                          <a
                            href={`mailto:${user.email}?subject=We're here for you - Nerd AI Support`}
                            className="px-4 py-2 bg-blue-500 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            📧 Send Email
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Payment History Section */}
        <div className="mt-8">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-6 w-6 text-green-600" />
                Payment History ({paymentTotalItems})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Search Bar & Filter */}
              <div className="mb-6 flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by name, email, order ID, or payment ID..."
                    value={paymentSearchQuery}
                    onChange={(e) => {
                      setPaymentSearchQuery(e.target.value);
                      setPaymentCurrentPage(1);
                    }}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
                
                {/* Status Filter */}
                <select
                  value={paymentStatusFilter}
                  onChange={(e) => {
                    setPaymentStatusFilter(e.target.value);
                    setPaymentCurrentPage(1);
                  }}
                  className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="all">All Status</option>
                  <option value="completed">Completed</option>
                  <option value="created">Pending</option>
                  <option value="failed">Failed</option>
                </select>
              </div>

              {/* Payment List Table */}
              {paymentsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="h-8 w-8 animate-spin text-green-600" />
                </div>
              ) : payments.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <CreditCard className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No payments found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan/Details</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {payments.map((payment) => (
                        <tr key={payment.id} className="hover:bg-gray-50">
                          <td className="px-4 py-4">
                            <div className="text-sm font-medium text-gray-900">{payment.user_name || 'N/A'}</div>
                            <div className="text-xs text-gray-500">{payment.user_email || 'N/A'}</div>
                            <div className="text-xs text-gray-400">{payment.mobile_number || 'No phone'}</div>
                          </td>
                          <td className="px-4 py-4">
                            <div className="text-sm font-medium text-gray-900">
                              {payment.type === 'event'
                                ? `Event: ${payment.event_title || payment.plan_name || 'Untitled'}`
                                : payment.plan_name || (payment.type === 'credit_purchase' ? `Credits (${payment.package})` : 'Plan Purchase')}
                            </div>
                            <div className="text-xs text-gray-500">
                              {payment.plan_duration || (payment.credits ? `${payment.credits} credits` : (payment.type === 'event' ? 'Event Registration' : ''))}
                            </div>
                            <div className="text-xs text-gray-400">Order: {payment.order_id?.slice(0, 20)}...</div>
                          </td>
                          <td className="px-4 py-4">
                            <div className="text-sm font-bold text-green-600">₹{payment.total_amount?.toLocaleString() || 0}</div>
                            {payment.discount_amount > 0 && (
                              <div className="text-xs text-orange-500">-₹{payment.discount_amount} ({payment.discount_percentage}%)</div>
                            )}
                            <div className="text-xs text-gray-400">GST: ₹{payment.gst_amount || 0}</div>
                          </td>
                          <td className="px-4 py-4">
                            <Badge className={`${
                              (payment.status === 'completed' || payment.status === 'paid') ? 'bg-green-100 text-green-800' :
                              payment.status === 'created' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {(payment.status === 'completed' || payment.status === 'paid') ? 'Completed' : 
                               payment.status === 'created' ? 'Pending' : 'Failed'}
                            </Badge>
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-500">
                            <div>{payment.completed_at ? new Date(payment.completed_at).toLocaleDateString('en-IN', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            }) : new Date(payment.created_at).toLocaleDateString('en-IN', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            })}</div>
                            <div className="text-xs text-gray-400">
                              {payment.completed_at ? new Date(payment.completed_at).toLocaleTimeString('en-IN', {
                                hour: '2-digit',
                                minute: '2-digit'
                              }) : new Date(payment.created_at).toLocaleTimeString('en-IN', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            {(payment.status === 'completed' || payment.status === 'paid') && (
                              <button
                                onClick={() => downloadInvoice(payment.order_id, payment.user_id)}
                                className="flex items-center gap-1 px-3 py-1.5 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm"
                              >
                                <Download className="h-4 w-4" />
                                Invoice
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {paymentTotalPages > 1 && (
                <div className="flex items-center justify-between mt-6 pt-4 border-t">
                  <div className="text-sm text-gray-500">
                    Page {paymentCurrentPage} of {paymentTotalPages} ({paymentTotalItems} payments)
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPaymentCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={paymentCurrentPage === 1}
                      className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    
                    {[...Array(Math.min(5, paymentTotalPages))].map((_, i) => {
                      const pageNum = paymentCurrentPage <= 3 
                        ? i + 1 
                        : paymentCurrentPage >= paymentTotalPages - 2 
                          ? paymentTotalPages - 4 + i 
                          : paymentCurrentPage - 2 + i;
                      
                      if (pageNum < 1 || pageNum > paymentTotalPages) return null;
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setPaymentCurrentPage(pageNum)}
                          className={`px-3 py-1 rounded-lg ${
                            paymentCurrentPage === pageNum 
                              ? 'bg-green-600 text-white' 
                              : 'border border-gray-300 hover:bg-gray-100'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    
                    <button
                      onClick={() => setPaymentCurrentPage(prev => Math.min(paymentTotalPages, prev + 1))}
                      disabled={paymentCurrentPage === paymentTotalPages}
                      className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Coupon Management Section */}
        <div className="mt-8">
          <AdminCouponManagement />
        </div>

        {/* Offer Zone Management Section */}
        <div className="mt-8">
          <AdminOfferZone />
        </div>

        {/* Plan Pricing Management */}
        <div className="mt-8" id="admin-plan-pricing-section">
          <AdminPlanPricing />
        </div>

        {/* Featured Carousel Management */}
        <div className="mt-8">
          <AdminCarousel />
        </div>

        {/* Bottom spacing */}
        <div className="pb-24"></div>
      </div>
    </div>
  );
};

export default AdminDashboard;